import { action } from 'mobx'
import { autorun } from 'mobx'
import { observable } from 'mobx'
import { TASK_STATE } from './task-state.constants'
import { TASK_POLL_INTERVAL } from './task-state.constants'
import Edition from './models/BaseContent/Edition'

export function taskStateStore(schedule, storage, assetSetList, queueCapture, queryCapture, abortCapture, readEditionVersion, captionsFromEdition, captionsFromChapter) {
  const TASK_STORAGE_KEY = 'pendingCaptures'
  const TASK_POLL_KEY = 'pollTask'

  const bindings = {
    err: observable.ref,
    added: observable.ref,
    changed: observable.ref,
    reset: action.bound,
    abortTask: action.bound,
    enqueueTask: action.bound,
    queryTaskState: action.bound,
  }

  const init = storage.has(TASK_STORAGE_KEY) ?
    storage.get(TASK_STORAGE_KEY, 'array') :
    []

  const state = {
    err: null,
    added: null,
    changed: null,
    pending: init,
    async abortTask(taskId) {
      const { pending } = this
      try {
        await abortCapture(taskId)
        const aborted = pending.find(taskState => taskState.taskId === taskId)
        aborted.taskStatus = TASK_STATE.ABORTED
        pending.remove(aborted)
        this.changed = aborted
      } catch (err) {
        this.err = err
      }
    },
    async enqueueTask(editionId, editionVersion, editionAssetPath, editionName) {
      const { pending } = this
      if (assetSetList.length === 0) {
        this.err = new Error("Failed to retrieve asset set list for determing program language. Refresh the page or try again later")
        return
      }
      try {
        const latest = false
        const assetSet = assetSetList.find(a => a.assetSetPath === editionAssetPath)
        const videoContentLang = assetSet.dialect.iso639 === 'en' ? 'English' : 'Spanish'
        const decoder = new TextDecoder
        const meta = await readEditionVersion(editionId, editionVersion, latest)
        const edition = Edition.create(meta.version.content)
        const editionVTT = decoder.decode(captionsFromEdition(edition))
        const chapterVTTs = edition.chapters
          .filter(shouldCapture)
          .map(c => decoder.decode(captionsFromChapter(c)))
          .map(vtt => vtt ? vtt : null)
        const captions = [ ...chapterVTTs, editionVTT ]

        const taskState = await queueCapture(editionId, editionVersion, editionName, editionAssetPath, videoContentLang, captions)
        const { taskStatus } = taskState
        switch (taskStatus) {
          case TASK_STATE.QUEUED:
          case TASK_STATE.INITIALIZING:
          case TASK_STATE.CAPTURING:
          case TASK_STATE.PROCESSING:
            pending.push(taskState)
            this.added = taskState
            return
          default:
            this.err = new Error('Encountered invalid state while attempting to queue edition version capture')
        }
      } catch (err) {
        this.err = new Error(`Encountered unexpected error attempting to enqueue capture task:\n${err.message}\n File a ticket if this continues`)
      }
    },
    async queryTaskState() {
      const { pending } = this
      if (!pending.length) return
      try {
        const [taskState,] = pending
        const { taskId } = taskState
        const next = await queryCapture(taskId)
        switch (next.taskStatus) {
          case TASK_STATE.ERROR:
            pending.remove(taskState)
            this.changed = next
            return
          case TASK_STATE.DONE:
          case TASK_STATE.ABORTED:
            taskState.taskStatus = next.taskStatus
            pending.remove(taskState)
            break
          case TASK_STATE.INITIALIZING:
          case TASK_STATE.CAPTURING:
          case TASK_STATE.PROCESSING:
            taskState.taskStatus = next.taskStatus
            break
          case TASK_STATE.REQUEUED:
            if (taskState.taskStatus === TASK_STATE.REQUEUED) return
            else taskState.taskStatus = next.taskStatus
            break
          case TASK_STATE.QUEUED:
            return
          default:
            this.err = new Error('Encountered invalid task state while polling for current state of edition capture - file an issue to address')
            return
        }
        this.changed = taskState
      } catch (err) {
        this.err = err
      }
    },
    reset() {
      this.err = null
      this.added = null
      this.changed = null
      this.pending.clear()
    },
  }

  const store = observable(state, bindings)

  schedule.poll(TASK_POLL_KEY, TASK_POLL_INTERVAL, () => store.queryTaskState())

  autorun(() => {
    const { pending } = store
    const { length } = pending
    if (length) storage.set(TASK_STORAGE_KEY, 'array', pending.toJSON())
    else storage.set(TASK_STORAGE_KEY, 'array', [])
  })

  return store
}

// N.B. match filter logic in ePlayer
// TODO replace with data type that exhausts
// capture instructions
function shouldCapture(chapter) {
      const sequences = []
      const chName = chapter.name
      if (!chapter.shouldCapture) return false
      if (chName.match(/[Ii]ntroducci[oó]n/)) return false
      if (chName.match(/[Ii]ntroduction/)) return false
      if (chName.match(/[Mm]enu/)) return false
      for (const s of chapter.sequences) {
        if (s.isMenu) continue
        if (!s.shouldCapture) continue
        sequences.push(s)
      }
      if (sequences.length) return true
      else return false
}
